import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2, CheckCircle2, Phone } from "lucide-react";
import { toast } from "sonner";
import { API, COUNTY_OPTIONS, PROPERTY_TYPE_OPTIONS, TIMELINE_OPTIONS } from "../lib/funnelData";
import { LegalLinks } from "./Legal";

const variants = {
  initial: { x: 24, opacity: 0 },
  animate: { x: 0, opacity: 1 },
  exit: { x: -24, opacity: 0 },
};

const inputCls = "w-full rounded-full border border-emeraldx-deep/15 bg-white px-4 py-3.5 text-base text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-oak/50 focus:border-oak/50 transition-shadow duration-150";
const optionCls = (active) =>
  `rounded-full border px-4 py-3.5 text-left transition-colors duration-150 active:scale-[0.98] ${
    active ? "border-emeraldx-deep bg-emeraldx-deep text-canvas" : "border-emeraldx-deep/15 bg-white text-stone-700 hover:border-emeraldx-deep/40"
  }`;

export const ValuationFunnel = ({ p, prefillAddress }) => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    address: prefillAddress || "",
    county: p.countyKey || "",
    property_type: p.typeKey || "land",
    acreage: 10,
    timeline: "",
    sms_consent: false,
    marketing_consent: false,
    full_name: "",
    email: "",
    phone: "",
  });

  useEffect(() => {
    if (prefillAddress) setForm((f) => ({ ...f, address: prefillAddress }));
  }, [prefillAddress]);

  useEffect(() => {
    setForm((f) => ({
      ...f,
      county: f.county || p.countyKey || f.county,
      property_type: p.typeKey || f.property_type,
    }));
  }, [p]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const next = () => {
    if (step === 1 && !form.address.trim()) return toast.error("Please enter your property address.");
    if (step === 3 && !form.timeline) return toast.error("Please choose your selling timeline.");
    setStep((s) => Math.min(s + 1, 4));
  };

  const submit = async () => {
    if (!form.full_name.trim()) return toast.error("Please enter your name.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return toast.error("Please enter a valid email address.");
    if (form.phone.replace(/\D/g, "").length < 7) return toast.error("Please enter a valid phone number.");
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          acreage: Number(form.acreage),
          campaign_source: p.source,
          utm_medium: p.utm_medium,
          utm_campaign: p.utm_campaign,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error("submit failed");
      setStep(5);
    } catch {
      toast.error("Something went wrong submitting your valuation. Please call (864) 314-2752.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="funnel" className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
      <div className="absolute inset-0 pattern-contour" />
      <div className="relative max-w-3xl mx-auto">
        <div className="text-center">
          <span className="badge-brand">Free · No obligation</span>
          <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-serif text-emeraldx-deep [text-wrap:balance]">What's your property worth?</h2>
          <span className="rule-gold mx-auto mt-6" />
          <p className="mt-4 text-base text-stone-600 [text-wrap:pretty]">Answer a few questions and I'll personally review your property — then follow up with you directly about its value. No automated numbers, no pressure.</p>
        </div>

        <div className="mt-8 rounded-md bg-white border border-emeraldx-deep/10 shadow-[0_20px_50px_-24px_rgba(27,26,24,0.35)] p-6 sm:p-8" data-testid="valuation-funnel">
          {step <= 4 && (
            <div className="flex items-center gap-2 mb-7" data-testid="funnel-progress">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className={`h-1 flex-1 transition-colors duration-200 ${i <= step ? "bg-oak" : "bg-stone-200"}`} />
              ))}
              <span className="ml-2 text-[11px] font-mono uppercase tracking-[0.2em] text-stone-500 tabular-nums">{step}/4</span>
            </div>
          )}

          <AnimatePresence mode="wait" initial={false}>
            {step === 1 && (
              <motion.div key="s1" data-testid="funnel-step-1" variants={variants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.25 }}>
                <h3 className="text-lg sm:text-xl font-serif text-emeraldx-deep">Where is the property?</h3>
                <div className="mt-5 space-y-5">
                  <div>
                    <label htmlFor="f-address" className="block text-[11px] font-mono uppercase tracking-[0.2em] text-stone-500 mb-2">Property address</label>
                    <input id="f-address" data-testid="funnel-address-input" className={inputCls} placeholder="e.g. 1425 Amity Road, Anderson SC" value={form.address} onChange={(e) => set("address", e.target.value)} />
                  </div>
                  <div>
                    <span className="block text-[11px] font-mono uppercase tracking-[0.2em] text-stone-500 mb-2">County</span>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="funnel-county-options">
                      {COUNTY_OPTIONS.slice(0, 6).map((c) => (
                        <button key={c.value} type="button" data-testid={`funnel-county-${c.value}`} onClick={() => set("county", c.value)} className={optionCls(form.county === c.value) + " text-sm font-semibold"}>
                          {c.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="s2" data-testid="funnel-step-2" variants={variants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.25 }}>
                <h3 className="text-lg sm:text-xl font-serif text-emeraldx-deep">Tell us about the property</h3>
                <div className="mt-5 space-y-6">
                  <div className="grid grid-cols-2 gap-2" data-testid="funnel-type-options">
                    {PROPERTY_TYPE_OPTIONS.map((t) => (
                      <button key={t.value} type="button" data-testid={`funnel-type-${t.value}`} onClick={() => set("property_type", t.value)} className={optionCls(form.property_type === t.value) + " text-sm font-semibold"}>
                        {t.label}
                      </button>
                    ))}
                  </div>
                  <div>
                    <label htmlFor="f-acreage" className="block text-[11px] font-mono uppercase tracking-[0.2em] text-stone-500 mb-2">
                      Approximate acreage — <span className="text-emeraldx-deep tabular-nums" data-testid="funnel-acreage-value">{form.acreage} ac</span>
                    </label>
                    <input id="f-acreage" data-testid="funnel-acreage-slider" type="range" min="0" max="500" step="1" value={form.acreage} onChange={(e) => set("acreage", e.target.value)} className="w-full accent-[#B08D57]" />
                    <div className="flex justify-between text-[11px] font-mono text-stone-400"><span>0</span><span>250</span><span>500+</span></div>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="s3" data-testid="funnel-step-3" variants={variants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.25 }}>
                <h3 className="text-lg sm:text-xl font-serif text-emeraldx-deep">When are you thinking of selling?</h3>
                <div className="mt-5 grid sm:grid-cols-2 gap-2" data-testid="funnel-timeline-options">
                  {TIMELINE_OPTIONS.map((t) => (
                    <button key={t.value} type="button" data-testid={`funnel-timeline-${t.value}`} onClick={() => set("timeline", t.value)} className={optionCls(form.timeline === t.value)}>
                      <span className="block text-sm font-semibold">{t.label}</span>
                      <span className={`block text-xs mt-0.5 ${form.timeline === t.value ? "text-canvas/70" : "text-stone-400"}`}>{t.hint}</span>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {step === 4 && (
              <motion.div key="s4" data-testid="funnel-step-4" variants={variants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.25 }}>
                <h3 className="text-lg sm:text-xl font-serif text-emeraldx-deep">Where should I send your valuation?</h3>
                <div className="mt-5 space-y-4">
                  <div>
                    <label htmlFor="f-name" className="block text-[11px] font-mono uppercase tracking-[0.2em] text-stone-500 mb-2">Full name</label>
                    <input id="f-name" data-testid="funnel-name-input" className={inputCls} placeholder="Your name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="f-email" className="block text-[11px] font-mono uppercase tracking-[0.2em] text-stone-500 mb-2">Email</label>
                      <input id="f-email" data-testid="funnel-email-input" type="email" className={inputCls} placeholder="you@email.com" value={form.email} onChange={(e) => set("email", e.target.value)} />
                    </div>
                    <div>
                      <label htmlFor="f-phone" className="block text-[11px] font-mono uppercase tracking-[0.2em] text-stone-500 mb-2">Phone</label>
                      <input id="f-phone" data-testid="funnel-phone-input" type="tel" className={inputCls} placeholder="(864) 555-0123" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 rounded-xl border border-emeraldx-deep/15 bg-surface/60 p-4">
                      <input
                        id="f-sms-consent"
                        type="checkbox"
                        data-testid="funnel-sms-consent"
                        checked={form.sms_consent}
                        onChange={(e) => set("sms_consent", e.target.checked)}
                        className="mt-0.5 h-4 w-4 shrink-0 accent-[#B08D57]"
                      />
                      <label htmlFor="f-sms-consent" className="text-xs text-stone-500 leading-relaxed">
                        I agree to receive customer care text messages and calls from <span className="font-semibold text-emeraldx-deep">Cann Realty</span> about my valuation and related real estate services (follow-ups, scheduling, property questions). Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for help. Consent is not a condition of purchase.
                      </label>
                    </div>
                    <div className="flex items-start gap-3 rounded-xl border border-emeraldx-deep/15 bg-surface/60 p-4">
                      <input
                        id="f-marketing-consent"
                        type="checkbox"
                        data-testid="funnel-marketing-consent"
                        checked={form.marketing_consent}
                        onChange={(e) => set("marketing_consent", e.target.checked)}
                        className="mt-0.5 h-4 w-4 shrink-0 accent-[#B08D57]"
                      />
                      <label htmlFor="f-marketing-consent" className="text-xs text-stone-500 leading-relaxed">
                        Separately, I agree to receive occasional marketing and promotional text messages from <span className="font-semibold text-emeraldx-deep">Cann Realty</span>. Message frequency varies. Message and data rates may apply. Reply STOP to opt out.
                      </label>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="funnel-consent">
                    <p className="text-xs text-stone-400 leading-relaxed">Both boxes are optional and never pre-checked. Submitting this form authorizes Cann Realty to contact you about your request.</p>
                    <LegalLinks suffix="-form" />
                  </div>
                </div>
              </motion.div>
            )}

            {step === 5 && (
              <motion.div key="s5" data-testid="funnel-step-5" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="text-center py-4">
                <span className="mx-auto w-14 h-14 bg-oak/15 flex items-center justify-center">
                  <CheckCircle2 size={28} className="text-emeraldx-deep" />
                </span>
                <h3 className="mt-5 text-2xl font-serif text-emeraldx-deep" data-testid="funnel-confirmation">Thank you, {form.full_name.split(" ")[0]}</h3>
                <p className="mt-4 text-sm sm:text-base text-stone-600 leading-relaxed [text-wrap:pretty]">
                  Your property details are on my desk. I'll personally review your property — nearby sales, market conditions, and its features — and follow up with you directly about its value.
                </p>
                <a href="tel:8643142752" data-testid="funnel-call-button" className="mt-6 inline-flex items-center gap-2 rounded-full bg-emeraldx-deep hover:bg-emeraldx-deeper text-canvas px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] transition-colors duration-150 active:scale-[0.96]">
                  <Phone size={15} /> Talk to Jody now
                </a>
              </motion.div>
            )}
          </AnimatePresence>

          {step <= 4 && (
            <div className="mt-7 flex items-center justify-between gap-3">
              <button
                type="button"
                data-testid="funnel-prev-btn"
                onClick={() => setStep((s) => Math.max(s - 1, 1))}
                disabled={step === 1}
                className="inline-flex items-center gap-1.5 px-4 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-stone-500 hover:bg-surface disabled:opacity-0 transition-colors duration-150"
              >
                <ArrowLeft size={14} /> Back
              </button>
              {step < 4 ? (
                <button
                  type="button"
                  data-testid="funnel-next-btn"
                  onClick={next}
                  className="inline-flex items-center gap-2 rounded-full bg-emeraldx-deep hover:bg-emeraldx-deeper text-canvas px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] transition-colors duration-150 active:scale-[0.96]"
                >
                  Continue <ArrowRight size={14} />
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="funnel-submit-btn"
                  onClick={submit}
                  disabled={submitting}
                  className="inline-flex items-center gap-2 rounded-full bg-oak hover:bg-oak-dark text-white px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] transition-colors duration-150 active:scale-[0.96] disabled:opacity-60"
                >
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
                  {submitting ? "Sending…" : "Get My Free Valuation"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
